import { resourcesLanguageEnglish } from '@ragu-components/ord-providers';

import type { UserResourcesTranslationMessages } from './userResourcesTranslationMessages';

export const userResourcesLanguageEnglish: UserResourcesTranslationMessages = {
  users: {
    name: 'Users',
    fields: {
      fileUploadHint: 'Click or drag a file here to upload an image for this user',
      id: 'ID',
      email: 'Email',
      mappings: 'Mappings',
      password: 'Password',
      username: 'Username',
      firstname: 'First Name',
      middlename: 'Middle Name',
      lastname: 'Last Name',
      language: 'Language',
      timezone: 'Timezone',
      account_disabled: 'Account Disabled',
      account_enabled: 'Account Enabled',
      enabling: 'Enabling Account',
      required_actions: 'Required Actions',
      required_actions_values: {
        change_pass: 'Change Password',
        configure_totp: 'Configure TOTP',
      },
      acl_resources: 'Permissions',
      last_access_at: 'Latest login',
      password_last_change_at: 'Lastest password change',
      sessionexpire: 'Session Duration',
      acl_labels: { id: 'Permissions', name: 'Permissions', bulkUpdateLabel: 'Acl Labels' },
      labels: { id: 'Labels', name: 'Labels' },
      groups: { name: 'Groups' },
      mobility_configuration: 'Mobility Configuration',
      totp_enabled: 'TOTP Enabled',
      totp_last_used_at: 'TOTP last used',
      service_class: 'Service class',
      metadata: {
        identification_number: 'Identification number',
        shared_pause: 'Shared pause',
        call_forwarding_rules: 'Call forwarding rules',
        can_access_own_recordings: 'Can access own recordings',
        recall_enabled: 'Can use the recall service',
      },
      photo: {
        name: 'Profile Picture',
        description: 'Upload a profile picture. Max size is 1MB.',
        current: 'Current profile picture',
        too_large: 'Profile picture is too large',
        invalid: 'Profile picture is invalid',
      },
      assigned_lcr: 'LCR',
    },
    operations: {
      create: 'Create user',
      delete: 'Delete the user',
      edit: 'Edit user',
      sections: {
        personalData: 'Personal Data',
        userSettings: 'Settings',
        permissions: 'Permissions',
      },
      genPassword: 'Generate secure password',
    },
    helperTexts: {
      sessionexpire:
        'The user’s session timeout, in seconds, used to automatically expire the user’s authentication to some services',
      acl_labels: 'List of access labels for this user',
      recall_enabled:
        'The recall service will allow the selected user to book a recall request to a local extension, if the call to the extension is not successful',
    },
  },
  owner: {
    name: 'Owner',
  },
  groups: {
    name: 'Groups',
    fields: {
      id: 'ID',
      description: 'Description',
      name: 'Group name',
      number: 'Pickup number',
      labels: { id: 'Labels', name: 'Labels' },
      users: 'Users',
      acl_labels: 'ACL Labels',
      acl_resources: 'ACL Resources',
    },
    operations: {
      create: 'Create group',
      delete: 'Delete the group',
      edit: 'Edit group',
      sections: {
        groupsInfo: 'Group info',
        permissions: 'Permissions',
      },
    },
    helperTexts: {
      acl_labels: 'List of access labels for this group',
      number: 'If set, the group can be used as a call pickup group',
    },
  },
  extensions: {
    name: 'Extensions',
    fields: {
      labels: { id: 'Labels', name: 'Labels' },
      id: 'ID',
      number: 'Number',
      ringtime: 'Ring time',
      user: 'User',
    },
    operations: {
      create: 'Create extension',
      delete: 'Delete extension',
      edit: 'Edit extension',
      sections: {
        details: 'Extension details',
      },
    },
    helperTexts: {
      number: 'Extension number, the one that needs to be called to reach the device.',
      ringtime: 'Ring time in seconds',
    },
  },
  sip_identities: {
    name: 'SIP Identities',
    fields: {
      id: 'ID',
      assigned_sip_profile: 'SIP Profile',
      extension: 'Extension',
      labels: { id: 'Labels', name: 'Labels' },
      password: 'SIP Password',
      user: 'User',
      username: 'Username',
      device_types: 'Device types',
      enum_device_types: {
        mobile: 'Mobile',
        chat: 'Chat',
        desktop: 'Desktop',
        phone: 'Phone',
        web: 'Web',
      },
    },
    operations: {
      create: 'Create a SIP identity',
      delete: 'Delete SIP identity',
      edit: 'Edit SIP identity',
      sections: {
        details: 'SIP identity details',
      },
      genPassword: 'Generate secure SIP password',
    },
    helperTexts: {
      username: 'Sip Identity username, used to register to the system',
      password: 'Sip Identity password, used to register to the system',
    },
    errors: {
      usernameAlreadyExists: 'Sip Identity with this username already exists',
    },
  },
  hunting_groups: {
    name: 'Hunting groups',
    fields: {
      id: 'ID',
      name: 'Name',
      description: 'Description',
      labels: { id: 'Labels', name: 'Labels' },
      owner: 'Owner',
      user_numbers: 'User numbers',
    },
    operations: {
      create: 'Create Hunting group',
      delete: 'Delete Hunting group',
      edit: 'Edit Hunting group',
      sections: {
        details: 'Hunting group details',
      },
    },
    helperTexts: {
      owner: 'The user associated with this hunting group, which is also its owner',
    },
  },
  user_numbers: {
    name: 'User numbers',
    fields: {
      id: 'ID',
      description: 'Description',
      labels: { id: 'Labels', name: 'Labels' },
      number: 'Number',
      owner: 'Owner',
    },
    operations: {
      create: 'Create User number',
      delete: 'Delete User number',
      edit: 'Edit User number',
      sections: {
        details: 'User number details',
      },
    },
    helperTexts: {
      owner: 'The user associated with this number, which is also its owner',
    },
  },
  voicemails: {
    name: 'Voicemail',
    settingButtonLabel: 'Setup',
    fields: {
      labels: { id: 'Labels', name: 'Labels' },
      preferences: {
        enabled: 'Enabled',
        pin: 'PIN',
        quota: 'Quota (seconds)',
      },
    },
    helperTexts: {
      notConfigured: 'No voicemail settings yet. Enable editing to create a configuration.',
      settingsNotConfigured: 'Voicemail is not configured for your account. Contact an administrator to enable it',
    },
    actions: {
      enable: 'Configure voicemail settings',
    },
    notifications: {
      loading: 'Loading voicemail settings...',
      fetchError: 'Failed to load voicemail settings',
      createSuccess: 'Voicemail settings created',
      updateSuccess: 'Voicemail settings updated',
      updateError: 'Failed to save voicemail settings',
      requestError: 'Failed to retrieve voicemail settings',
    },
    validation: {
      pin: 'PIN must be 4 to 10 digits',
    },
  },
  conferences: {
    name: 'Conferences',
  },
  distribution_lists: {
    name: 'Distribution lists',
    operations: {
      create: 'Create Distribution list',
      delete: 'Delete Distribution list',
      edit: 'Edit Distribution list',
      sections: {
        details: 'Distribution list details',
      },
    },
  },
  contacts: resourcesLanguageEnglish.contacts,
  user_import: {
    name: 'User import',
    fields: {
      fileUploadHint: 'Click or drag a file here to upload a CSV file',
      overwrite: 'Overwrite existing users on match',
    },
    operations: {
      create: 'Import users',
      delete: 'Delete import',
    },
    notifications: {
      created: 'User import task created',
    },
  },
  tasks: {
    name: 'Tasks',
    actions: {
      download: 'Download Backup',
    },
    fields: {
      id: 'ID',
      type: 'Type',
      created_at: 'Created at',
      types: {
        users_import: 'Users import',
        users_export: 'Users export',
        backup_import: 'Backup import',
        backup_export: 'Backup export',
        contacts_import: 'Contacts import',
        contacts_export: 'Contacts export',
      },
      status: 'Status',
      statuses: {
        starting: 'Starting',
        running: 'Running',
        error: 'Error',
        done: 'Done',
      },
      result: {
        detail: 'Detail',
        title: 'Result',
        backupReady: 'The backup for domain %{realm} is ready to download.',
        backupImportDone: 'Import Complete',
        backupImportSuccess: 'The backup was successfully imported as domain %{realm}.',
      },
    },
  },
  autoprovisioning: {
    authentication: {
      name: 'Authentication',
      settingButtonLabel: 'Setup',
      description: 'When disabled, AP devices can auto-provision without credentials for the specified duration.',
      status: { enabled: 'Enabled', disabled: 'Disabled' },
      fields: { duration: 'Duration (seconds)', expires_at: 'Expires at', enabled: 'Status' },
      disableSection: 'Open authentication window',
      actions: { enable: 'Enable', disable: 'Disable' },
      notifications: {
        enableSuccess: 'Authentication enabled',
        enableError: 'Failed to enable authentication',
        disableSuccess: 'Authentication disabled',
        disableError: 'Failed to disable authentication',
        fetchError: 'Failed to load autoprovisioning authentication settings',
      },
    },
    settings: {
      name: 'Settings',
      fields: {
        outbound_proxy: 'Outbound proxy',
        outbound_backup_proxy: 'Backup outbound proxy',
      },
      notifications: {
        saveSuccess: 'Settings saved',
        saveError: 'Failed to save settings',
        fetchError: 'Failed to load settings',
      },
    },
  },
};
